import colors from '@sportninja/common/constants/appColors'
import type { AudienceGroupCounts } from '../components/AudienceBreakdownSection'
import type { AudienceReach } from './audienceReach'

export type AudienceGroupKey = keyof AudienceGroupCounts

/**
 * The eight audience groups, in the order every screen lists them.
 *
 * ONE array, because there used to be two: the donut's `CATEGORY_CONFIG` and the pre-send
 * confirmation's `GROUP_ROWS` held the same keys and the same labels in the same order,
 * maintained by hand in two files. Duplicated data with no test on either copy is what let
 * the two screens print different breakdowns of the same audience.
 *
 * A donut needs eight hues that stay apart at 10px legend-swatch size. `appColors` is a
 * semantic palette — five families (primary/success/info/warning/error) plus neutrals — so
 * pulling all eight from it would seat two blues and two yellows next to each other and
 * make the chart harder to read than the hexes it replaced. Palette tokens are used
 * wherever one genuinely matches the hue; the remaining three stay literal and named here
 * rather than adding chart-only hues to a global token file.
 */
export const AUDIENCE_GROUPS: {
  key: AudienceGroupKey
  label: string
  color: string
}[] = [
  { key: 'admin_users', label: 'Admins', color: colors.PRIMARY_200 },
  {
    key: 'league_admin_users',
    label: 'League Managers',
    color: colors.PRIMARY_75,
  },
  { key: 'official_users', label: 'Officials', color: colors.INFO_200 },
  { key: 'videographer_users', label: 'Videographers', color: '#bc37d3' },
  { key: 'player_users', label: 'Players', color: '#08e3ff' },
  { key: 'team_official_users', label: 'Team Officials', color: '#ffad33' },
  { key: 'following_users', label: 'Followers', color: colors.SUCCESS_100 },
  { key: 'owner_users', label: 'Owners', color: colors.ERROR_200 },
]

export type AudienceLegendRow = {
  key: AudienceGroupKey
  label: string
  color: string
  /** Channel-scoped reach — what the row SAYS. */
  value: number
}

/**
 * The breakdown rows, for the donut legend and for the confirmation that has to agree
 * with it. Both call this; neither decides for itself.
 *
 * Rows are chosen by MEMBERSHIP and valued by REACH, and the two halves are load-bearing
 * in opposite directions:
 *
 *  - Choosing by reach makes the legend vanish wholesale wherever reach is zero — every
 *    real organization outside production, where delivery is narrowed to @sportninja.com —
 *    leaving a blank ring and no breakdown at all.
 *
 *  - Choosing by the legacy `*_users` counts drops any group whose members ALL lack an
 *    account, because those counts only ever counted account holders. That is exactly the
 *    population SN-7901 exists to reach: an org of 131 people, 130 of them un-joined,
 *    rendered as one solid ring labelled "Owners: 1" beside a centre reading 131.
 *
 * `in_audience` is neither — it is the size of the group in the recipient list, summed
 * over the same three populations the centre's "of N in audience" counts. So the
 * legend keeps rendering under the domain guard AND cannot disagree with the
 * number in the middle.
 *
 * What each row SAYS still comes from reach, and reach alone. The legacy counts let one
 * person appear in several groups at once, while the resolver assigns each person exactly
 * one group, so pairing them read as "Officials: 38 of 37". The two are not comparable and
 * must not be shown as a ratio.
 */
export const audienceLegendRows = (reach: AudienceReach): AudienceLegendRow[] =>
  AUDIENCE_GROUPS.filter(
    ({ key }) => (reach.inAudienceByGroup[key] ?? 0) > 0
  ).map(({ key, label, color }) => ({
    key,
    label,
    color,
    value: reach.byGroup[key] ?? 0,
  }))
